import { useContext } from 'react';

import { CommentsTrackingContext } from '../contexts/CommentsTrackingContext';
import { CommentsTrackingContextValue } from '../types';

/**
 * Custom hook to access the comments tracking context
 *
 * @throws Error if used outside of CommentsTrackingProvider
 * @returns The comments tracking context value with state and methods
 *
 * @example
 * ```tsx
 * const {
 *   unreadCommentIds,
 *   totalUnreadComments,
 *   markCommentAsRead,
 *   setUnreadCommentIds,
 * } = useCommentsTracking();
 *
 * // Mark a comment as read
 * markCommentAsRead(123);
 *
 * // Update unread comment IDs
 * setUnreadCommentIds([1, 2, 3]);
 * ```
 */
export const useCommentsTracking = (): CommentsTrackingContextValue => {
  const context = useContext(CommentsTrackingContext);

  if (!context) {
    throw new Error('useCommentsTracking must be used within a CommentsTrackingProvider');
  }

  return context;
};
